// An athlete writing themselves a programme.
//
// Pure logic, paired with SelfProgramPage.js. The screen asks two questions that
// actually change the answer — what equipment do you have, and which days do you
// train — and everything here is about keeping those two honest before they reach
// the server.
//
// # WHY THE BOUNDS ARE ENFORCED HERE AND NOT ONLY ON THE SERVER
//
// The 2..6 session bound is the generation service's own, on a field the athlete
// never sees. Left to the server it comes back as a rejection about `sessions`
// after the form has been submitted; checked here it is a disabled button and a
// sentence about the question they are looking at.
//
// The server checks it too, and must: this is a convenience, not the guard.

import { WEEKDAYS } from './schedule';

/** The service's own bound on a week, mirrored from routes/drafts.go. */
export const MIN_DAYS = 2;
export const MAX_DAYS = 6;

/**
 * The equipment profiles, in increasing order of what they grant.
 *
 * The tokens must match models.EquipmentProfile exactly — the server refuses an
 * unrecognised one rather than defaulting, deliberately, because guessing here
 * is the one parse error with a physical consequence: somebody who answered
 * "bodyweight" must never be handed a barbell programme.
 */
export const EQUIPMENT_PROFILES = [
  {
    value: 'bodyweight',
    label: 'Just me',
    detail: 'No equipment at all.',
  },
  {
    value: 'minimal',
    label: 'Dumbbells and bands',
    detail: 'A pair of dumbbells, maybe some resistance bands.',
  },
  {
    value: 'home_gym',
    label: 'Home gym',
    detail: 'Barbell, rack, bench, dumbbells, somewhere to hang.',
  },
  {
    value: 'full_gym',
    label: 'Full gym',
    detail: 'Everything, including cables and machines.',
  },
];

/** A blank survey. Nothing is preselected except the days people actually pick. */
export const emptySurvey = {
  equipment: '',
  // Monday, Wednesday, Friday: the most common three-day week, and a starting
  // point somebody adjusts rather than a question they answer from scratch.
  days: [1, 3, 5],
  name: '',
  trainingAge: '',
  goals: '',
  injuries: '',
  notes: '',
};

/**
 * Toggle a training day.
 *
 * Returns a new sorted list. Sorted because the server places sessions on days
 * POSITIONALLY — day one of the block goes on the first day chosen — so an
 * unsorted list would put the second session before the first.
 */
export function toggleDay(days, day) {
  const current = days ?? [];
  const next = current.includes(day)
    ? current.filter((d) => d !== day)
    : [...current, day];
  return next.sort((a, b) => a - b);
}

/**
 * Why the survey cannot be submitted yet, or null when it can.
 *
 * One reason at a time, in the order somebody fills the form in. A list of every
 * problem at once is a wall of red on a screen where two of the three fields have
 * not been reached yet.
 */
export function surveyProblem(survey) {
  if (!survey?.equipment) {
    return 'Tell us what you have to train with.';
  }
  if (!EQUIPMENT_PROFILES.some((p) => p.value === survey.equipment)) {
    return 'Pick one of the equipment options.';
  }

  const days = survey.days ?? [];
  if (days.length < MIN_DAYS) {
    return `Pick at least ${MIN_DAYS} days a week.`;
  }
  if (days.length > MAX_DAYS) {
    return `Pick at most ${MAX_DAYS} days a week.`;
  }
  return null;
}

/** Whether the survey is complete enough to send. */
export function canSubmit(survey) {
  return surveyProblem(survey) === null;
}

/**
 * The request body.
 *
 * Sends the whole survey rather than only the required fields: the server stores
 * it on the draft row and reads the equipment and the days back at ACTIVATION,
 * minutes later, on a different request. Dropping a field here loses it for good.
 *
 * Empty optional fields are omitted rather than sent blank, so the server's own
 * defaults apply instead of being overwritten with "".
 */
export function surveyRequest(survey) {
  const body = {
    equipment: survey.equipment,
    days: [...(survey.days ?? [])].sort((a, b) => a - b),
  };
  for (const field of ['name', 'trainingAge', 'goals', 'injuries', 'notes']) {
    const value = (survey[field] ?? '').trim();
    if (value) body[field] = value;
  }
  return body;
}

/**
 * The days the athlete actually chose, from the server when it says.
 *
 * # WHY NOT JUST THE FORM STATE
 *
 * The page survives a reload, and a reloaded page has no memory of the survey —
 * its state is emptySurvey, whose days default to Mon/Wed/Fri. Rendering that as
 * "on Mondays, Wednesdays and Fridays" to somebody who picked Tuesday and
 * Thursday is a confident, specific, wrong sentence, and nothing about it looks
 * like a bug.
 *
 * The server echoes the stored survey back on every response, so its answer wins
 * whenever there is one.
 */
export function chosenDays(state, survey) {
  const fromServer = state?.days;
  if (Array.isArray(fromServer) && fromServer.length > 0) return fromServer;
  return survey?.days ?? [];
}

/** Whether a draft is still being written. */
export function isGenerating(state) {
  return state?.status === 'pending';
}

/** Whether a draft is ready to be turned into a programme. */
export function isReady(state) {
  return state?.status === 'ready';
}

/** Whether generation failed. */
export function hasFailed(state) {
  return state?.status === 'failed';
}

/*
 * What the athlete's own training contributed to this block.
 *
 * # WHY THIS IS ON THE SCREEN AT ALL
 *
 * The loop closes upstream: a second block is written from the first. But a
 * block that silently used somebody's log is indistinguishable from one that
 * ignored it, and an athlete who cannot tell has no reason to keep logging —
 * which is the retention argument in docs/mvp-audit-2026-09-23.md §6. The point
 * of this sentence is to make logging visibly pay.
 *
 * # WHY IT NAMES ASPECTS AND NOT VALUES
 *
 * "Your training chose Intensification" asks the reader to know what that means
 * before the sentence can land, and the vocabulary is the service's, not
 * theirs. The aspects are the part somebody can check against their own
 * experience: they DID train hard for six weeks, so "how much work it carries"
 * is a claim they can agree or disagree with.
 */
const CONTRIBUTIONS = [
  ['phase', 'which phase you are in'],
  ['sq', 'what quality it trains'],
  ['volumeTier', 'how much work it carries'],
  ['focus', 'what it focuses on'],
  ['trainingAge', 'how advanced it is'],
];

/**
 * The blocks a recommendation was read from, in words.
 *
 * Nothing is claimed below one. A recommendation exists only for an athlete the
 * service already knows, so a count of zero means the drafts behind them were
 * abandoned rather than committed — their log was still read, and "the 0 blocks
 * you have logged" would be a worse sentence than a vaguer true one.
 */
export function historySource(blocksBehind) {
  if (!blocksBehind || blocksBehind < 1) return 'your logged training';
  if (blocksBehind === 1) return 'the block you have logged';
  return `the ${blocksBehind} blocks you have logged`;
}

/**
 * The sentence telling the athlete their log wrote this, or null.
 *
 * # WHY NULL IS THE COMMON CASE AND SAYS NOTHING
 *
 * Absent on a first block, and absent when the service could not be reached.
 * The temptation is a consolation line — "written from what you told us" — and
 * it is wrong twice: it greets a first-timer with an absence they had not
 * noticed, and on the failure path it reads as reassurance about a fallback
 * nobody mentioned. A screen that is already working says nothing here.
 *
 * Nothing is inferred from the block number. A third block USUALLY read a
 * history, and the occasions it did not are exactly the ones where the service
 * was down — so a guess would claim the loop precisely when it had broken.
 */
export function historyNote(state) {
  const applied = state?.recommendation;
  if (!applied) return null;

  const contributions = CONTRIBUTIONS
    .filter(([field]) => applied[field])
    .map(([, label]) => label);
  if (contributions.length === 0) return null;

  return { source: historySource(applied.blocksBehind), contributions };
}

/** A list of phrases as prose: "a", "a and b", "a, b and c". */
export function proseList(items) {
  if (items.length <= 1) return items[0] ?? '';
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/** The weekday names for a list of day numbers, in order. */
export function dayNames(days) {
  return [...(days ?? [])]
    .sort((a, b) => a - b)
    .map((day) => WEEKDAYS.find((w) => w.value === day)?.name)
    .filter(Boolean);
}

/**
 * "Mondays, Wednesdays and Fridays" — the week, as somebody would say it.
 */
export function daysLabel(days) {
  const names = dayNames(days).map((name) => `${name}s`);
  if (names.length === 0) return '';
  if (names.length === 1) return names[0];
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/**
 * When the athlete actually next trains, as a sentence.
 *
 * # THE MOST LIKELY "IT'S BROKEN" REPORT
 *
 * The block starts today, but nothing is scheduled before the start date and
 * sessions only fall on the days they chose. Somebody who answers this on a
 * Tuesday having picked Thursdays has a legitimately empty week for two days —
 * and having just been told their programme is ready, an empty week reads as a
 * failure. So the success message says when, rather than leaving it to be
 * discovered.
 *
 * The date comes from the server, which derives it through the same
 * models.ScheduleFor the calendar itself uses. Computing it here would be a
 * second implementation, free to disagree with the week it is describing.
 */
export function startsLabel(activation, today = new Date()) {
  const first = activation?.firstSessionDate;
  if (!first) return null;

  const todayISO = isoDate(today);
  if (first === todayISO) return 'Your first session is today.';

  const date = parseISODate(first);
  if (!date) return null;

  const dayName = WEEKDAYS[date.getUTCDay()]?.name ?? '';
  const tomorrow = isoDate(new Date(today.getTime() + 24 * 60 * 60 * 1000));
  if (first === tomorrow) return `Your first session is tomorrow, ${dayName}.`;

  return `Your first session is ${dayName} ${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]}.`;
}

/**
 * Where "See my week" goes: the week the block was just written in.
 *
 * The button is labelled "my week", and the week surface is the one screen that
 * IS a week — the days laid out with what each asks for. `/training` is the
 * athlete's dashboard, not a week, so a "week" button landing there kept a
 * promise it did not make. The activation carries the programme and phase ids
 * for exactly this (ActivationResult.PhaseID has no other reader), so this is
 * completing a route the wire format already anticipated.
 *
 * Returns null when either id is missing, so the caller can fall back rather
 * than build `/programs/5/phases/undefined/week`.
 */
export function weekHref(activation) {
  const programId = activation?.programId;
  const phaseId = activation?.phaseId;
  if (!programId || !phaseId) return null;
  return `/programs/${programId}/phases/${phaseId}/week`;
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** YYYY-MM-DD in the reader's own timezone. */
function isoDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Parse YYYY-MM-DD as a UTC date.
 *
 * UTC, not local: `new Date('2026-09-17')` is already parsed as UTC midnight, and
 * reading it back with getDate() in a zone behind UTC returns the 16th. Every
 * calendar date in this system is UTC midnight (models.CalendarDate) and reading
 * one in local time is how a session moves a day for half the world.
 */
function parseISODate(iso) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso ?? '');
  if (!match) return null;
  return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
}

/**
 * What the repair pass changed, as sentences.
 *
 * Shown rather than swallowed: an athlete handed a programme thinner or
 * different from the one that was generated is owed the reason. A substitution
 * with no `to` is a line that was removed, which is a different sentence.
 */
export function substitutionLabels(substitutions) {
  return (substitutions ?? []).map((change) => {
    if (!change.to) {
      return `Took out ${change.from} — ${change.reason}.`;
    }
    return `Swapped ${change.from} for ${change.to} — ${change.reason}.`;
  });
}

export { WEEKDAYS };
